/**
 * Pagina Inbox principale: 3 pannelli + ricerca + AI search.
 * v2: aggiunto polling automatico ogni 15s come fallback se WebSocket
 * non funziona, e auto-correzione del selectedAccountId se nullo.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Search, Sparkles, X, Filter, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { InboxSidebar } from '@/components/layout/InboxSidebar';
import { MessageList } from '@/components/inbox/MessageList';
import { MessageViewer } from '@/components/inbox/MessageViewer';
import { ShortcutsHelp } from '@/components/shortcuts/ShortcutsHelp';
import { SpamView } from '@/components/spam/SpamView';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useInbox } from '@/stores/inbox.store';
import { useComposer } from '@/stores/composer.store';
import { messagesApi, searchApi, threadsApi, accountsApi, type MessageListItem } from '@/lib/api/endpoints';
import { useKeyboardShortcuts } from '@/lib/hooks/useKeyboardShortcuts';

export function InboxPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { selectedAccountId, selectedFolderRole, selectedCategory, selectedLabel, selectedUrgency,
          unreadOnly, starredOnly, selectedMessageId, selectMessage, setAccount } = useInbox();

  const openCompose = useComposer((s) => s.openCompose);
  const [searchQuery, setSearchQuery] = useState('');
  // Il testo su cui si cerca davvero: si aspetta un attimo fra un tasto e
  // l'altro, altrimenti si interroga il server a ogni lettera.
  const [cercato, setCercato] = useState('');
  // La domanda per cui si è chiesta la risposta dell'AI (Invio o il tasto).
  const [domandaAI, setDomandaAI] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);

  // Filter starred from query string
  const starredFromUrl = searchParams.get('starred') === '1';
  const effectiveStarred = starredOnly || starredFromUrl;

  // FIX: se selectedAccountId è null all'avvio, fetch accounts e prendi il primo
  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list(),
  });

  useEffect(() => {
    const accounts = accountsQuery.data?.accounts;
    if (!accounts || accounts.length === 0) return;
    // L'id selezionato e persistito nel browser: se punta a un account
    // eliminato/ricollegato va sostituito, altrimenti tutte le liste
    // interrogano un account inesistente e restano vuote.
    const isValid = !!selectedAccountId && accounts.some((a) => a.id === selectedAccountId);
    if (!isValid) {
      const def = accounts.find((a) => a.is_default) ?? accounts[0];
      if (def) setAccount(def.id);
    }
  }, [selectedAccountId, accountsQuery.data, setAccount]);

  // Lista messaggi - con polling e refetch on focus
  const messagesQuery = useQuery({
    queryKey: ['messages', selectedAccountId, selectedFolderRole, selectedCategory, selectedLabel, selectedUrgency, unreadOnly, effectiveStarred],
    queryFn: () =>
      messagesApi.list({
        accountId: selectedAccountId!,
        folderRole: selectedFolderRole,
        limit: 50,
        category: selectedCategory ?? undefined,
        label: selectedLabel ?? undefined,
        urgency: selectedUrgency ?? undefined,
        unreadOnly,
        starredOnly: effectiveStarred,
      }),
    enabled: !!selectedAccountId,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    const t = setTimeout(() => setCercato(searchQuery.trim()), 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const inRicerca = cercato.length >= 2;

  // Ricerca per parole: nessuna AI di mezzo, risponde in un attimo.
  const ricerca = useQuery({
    queryKey: ['search', selectedAccountId, cercato],
    queryFn: () => searchApi.keyword(cercato, selectedAccountId ?? undefined),
    enabled: !!selectedAccountId && inRicerca,
    placeholderData: (prec) => prec, // i risultati di prima restano finché arrivano i nuovi
  });

  // La risposta dell'AI: solo quando la si chiede, perché è l'unica lenta.
  const risposta = useQuery({
    queryKey: ['search-answer', selectedAccountId, domandaAI],
    queryFn: () => searchApi.answer({ query: domandaAI, accountId: selectedAccountId ?? undefined }),
    enabled: !!selectedAccountId && domandaAI.length > 2,
    staleTime: 5 * 60_000,
  });
  const rispostaViva = domandaAI === cercato;
  const fonti = rispostaViva ? risposta.data?.sources ?? [] : [];

  const trovati = ricerca.data?.messages ?? [];
  const messages = useMemo(() => {
    if (!inRicerca) return messagesQuery.data?.messages ?? [];
    // Se le parole non trovano niente ma l'AI ha capito lo stesso di cosa si
    // parlava, si mostrano le email da cui ha tratto la risposta.
    return trovati.length > 0 ? trovati : fonti;
  }, [inRicerca, trovati, fonti, messagesQuery.data]);

  // Si parte da quello che c'è scritto adesso, non dal testo di un attimo fa:
  // chi scrive e preme subito Invio chiede la risposta su quello che vede.
  function chiediAllAI() {
    const q = searchQuery.trim();
    if (q.length > 2) {
      setCercato(q);
      setDomandaAI(q);
    }
  }
  function pulisciRicerca() {
    setSearchQuery('');
    setCercato('');
    setDomandaAI('');
  }

  // Tasto "Aggiorna": forza la sync IMAP della casella (corsia veloce ~4s lato server),
  // poi ricarica la lista. Utile se una mail tarda ad arrivare.
  async function handleRefresh() {
    if (!selectedAccountId || refreshing) return;
    setRefreshing(true);
    try {
      await accountsApi.forceSync(selectedAccountId);
      await new Promise((r) => setTimeout(r, 4500));
      await messagesQuery.refetch();
    } catch {
      // best-effort: il polling automatico (15s) resta come rete di sicurezza
    } finally {
      setRefreshing(false);
    }
  }

  // Keyboard navigation
  function nextMessage() {
    if (messages.length === 0) return;
    const idx = messages.findIndex((m) => m.id === selectedMessageId);
    const next = messages[Math.min(idx + 1, messages.length - 1)];
    if (next) selectMessage(next.id);
  }
  function prevMessage() {
    if (messages.length === 0) return;
    const idx = messages.findIndex((m) => m.id === selectedMessageId);
    const prev = messages[Math.max(idx - 1, 0)];
    if (prev) selectMessage(prev.id);
  }

  function reply() {
    const msg = messages.find((m) => m.id === selectedMessageId);
    if (!msg) return;
    openCompose({
      threadId: msg.thread_id,
      inReplyToMessageId: msg.id,
      to: msg.from_address ? [{ name: msg.from_address.name, address: msg.from_address.email }] : [],
      subject: `Re: ${msg.subject || ''}`.replace(/^Re: Re: /, 'Re: '),
    });
  }

  async function archiveSelected() {
    const msg = messages.find((m) => m.id === selectedMessageId);
    if (!msg) return;
    await threadsApi.archive(msg.thread_id);
    nextMessage();
  }
  async function trashSelected() {
    const msg = messages.find((m) => m.id === selectedMessageId);
    if (!msg) return;
    await threadsApi.trash(msg.thread_id);
    nextMessage();
  }
  async function starSelected() {
    const msg = messages.find((m) => m.id === selectedMessageId);
    if (!msg) return;
    const next = !(msg.is_starred || msg.thread_starred);
    await threadsApi.star(msg.thread_id, next);
  }

  useKeyboardShortcuts({
    onNext: nextMessage,
    onPrev: prevMessage,
    onReply: reply,
    onArchive: () => void archiveSelected(),
    onTrash: () => void trashSelected(),
    onStar: () => void starSelected(),
    onShowHelp: () => setShowHelp(true),
    onFocusSearch: () => searchInputRef.current?.focus(),
  });

  const isLoading = inRicerca ? ricerca.isLoading : messagesQuery.isLoading;
  const folderTitle: Record<string, string> = {
    inbox: 'Inbox',
    sent: 'Inviati',
    drafts: 'Bozze',
    trash: 'Cestino',
    spam: 'Spam',
    archive: 'Archivio',
  };
  const title = selectedLabel
    ? `Etichetta: ${selectedLabel}`
    : selectedCategory
    ? `Categoria: ${selectedCategory}`
    : effectiveStarred
    ? 'Speciali'
    : folderTitle[selectedFolderRole] ?? 'Inbox';

  // La cartella Spam non è un elenco di messaggi come gli altri: ogni voce ha il
  // giudizio del filtro e le sue azioni. Si cercano le parole solo nella posta vera.
  if (selectedFolderRole === 'spam' && selectedAccountId && !inRicerca) {
    return (
      <div className="h-screen flex bg-background">
        <InboxSidebar />
        <SpamView accountId={selectedAccountId} />
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-background">
      <InboxSidebar />

      <div className="flex-1 flex min-w-0">
        {/* List pane */}
        <div className="w-[420px] border-r flex flex-col bg-background min-w-0">
          {/* Ricerca: le parole mentre si scrive, l'AI su richiesta */}
          <div className="p-3 border-b">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                chiediAllAI();
              }}
              className="relative"
            >
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder="Cerca nella posta (premi /)"
                className="pl-9 pr-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') pulisciRicerca(); }}
              />
              {searchQuery.length > 0 && (
                <button
                  type="button"
                  onClick={pulisciRicerca}
                  title="Pulisci (Esc)"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </form>
            {inRicerca && (
              <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {ricerca.isFetching && trovati.length === 0
                    ? 'Cerco…'
                    : `${messages.length} ${messages.length === 1 ? 'conversazione' : 'conversazioni'}`}
                </span>
                <button
                  type="button"
                  onClick={chiediAllAI}
                  disabled={risposta.isFetching && rispostaViva}
                  className="inline-flex items-center gap-1 text-primary hover:underline disabled:opacity-60 disabled:no-underline"
                >
                  <Sparkles className="h-3 w-3" />
                  {risposta.isFetching && rispostaViva ? 'Leggo le email…' : 'Chiedi all’AI (Invio)'}
                </button>
              </div>
            )}
          </div>

          {/* La risposta dell'AI, sopra i risultati */}
          <AnimatePresence>
            {inRicerca && rispostaViva && (risposta.isFetching || risposta.data?.answer) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-primary/5 border-b text-sm overflow-hidden"
              >
                <div className="p-3">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-primary mb-2">
                    <Sparkles className="h-3 w-3" /> Risposta AI
                  </div>
                  {risposta.isFetching ? (
                    <div className="space-y-2" aria-label="Sto leggendo le email">
                      <div className="h-3 rounded bg-primary/10 animate-pulse" />
                      <div className="h-3 rounded bg-primary/10 animate-pulse w-4/5" />
                    </div>
                  ) : (
                    <RispostaAI
                      testo={risposta.data!.answer!}
                      fonti={risposta.data!.sources}
                      onApri={selectMessage}
                    />
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Header */}
          <div className="px-4 py-2 border-b text-xs text-muted-foreground flex items-center justify-between">
            <span className="truncate">
              {inRicerca ? `Risultati per «${cercato}»` : `${title} - ${messages.length} messaggi`}
            </span>
            <div className="flex items-center gap-3">
              {(selectedCategory || selectedUrgency || selectedLabel) && (
                <button
                  onClick={() => {
                    navigate('/');
                    useInbox.getState().resetFilters();
                  }}
                  className="hover:text-foreground inline-flex items-center gap-1"
                >
                  <X className="h-3 w-3" /> Pulisci filtri
                </button>
              )}
              <button
                onClick={handleRefresh}
                disabled={refreshing || !selectedAccountId}
                title="Aggiorna (controlla nuova posta)"
                className="hover:text-foreground inline-flex items-center gap-1 disabled:opacity-60"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Aggiorno…' : 'Aggiorna'}
              </button>
            </div>
          </div>

          <div ref={messageListRef} className="flex-1 overflow-hidden flex flex-col">
            <MessageList
              messages={messages}
              selectedId={selectedMessageId}
              onSelect={selectMessage}
              loading={isLoading}
              cercato={inRicerca ? cercato : undefined}
              onChiediAllAI={chiediAllAI}
            />
          </div>
        </div>

        {/* Detail pane */}
        {selectedMessageId ? (
          <MessageViewer
            messageId={selectedMessageId}
            accountId={selectedAccountId!}
            onClose={() => selectMessage(null)}
          />
        ) : (
          <EmptyDetailPane />
        )}
      </div>

      <ShortcutsHelp open={showHelp} onOpenChange={setShowHelp} />
    </div>
  );
}

/**
 * La risposta dell'AI. I numeri fra parentesi quadre diventano pulsanti che
 * aprono l'email da cui viene quel pezzo: chi legge può controllare, invece
 * di fidarsi.
 */
function RispostaAI({ testo, fonti, onApri }: {
  testo: string;
  fonti: MessageListItem[];
  onApri: (id: string) => void;
}) {
  // Lo spazio prima del riferimento lo mette già il pulsante: senza, «2026 [1].»
  // verrebbe con uno spazio staccato prima del punto.
  const pezzi = testo.replace(/\s+\[(\d+)\]/g, '[$1]').split(/\[(\d+)\]/g);
  return (
    <div className="space-y-2">
      <p className="leading-relaxed whitespace-pre-line">
        {pezzi.map((pezzo, i) => {
          if (i % 2 === 0) return <span key={i}>{pezzo}</span>;
          const n = Number(pezzo);
          const fonte = fonti[n - 1];
          if (!fonte) return <span key={i}>[{pezzo}]</span>;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onApri(fonte.id)}
              title={fonte.subject || '(senza oggetto)'}
              className="mx-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded bg-primary/15 px-1 align-baseline text-[10px] font-medium text-primary hover:bg-primary/25"
            >
              {n}
            </button>
          );
        })}
      </p>
      {fonti.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {fonti.slice(0, 4).map((f, i) => (
            <button
              key={f.id}
              type="button"
              onClick={() => onApri(f.id)}
              className="max-w-[12rem] truncate rounded-full border bg-background/60 px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground"
            >
              {i + 1}. {f.subject || '(senza oggetto)'}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyDetailPane() {
  return (
    <div className="flex-1 flex items-center justify-center text-muted-foreground bg-muted/10">
      <div className="text-center max-w-md px-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
          <Sparkles className="h-8 w-8 text-primary" />
        </div>
        <div className="text-lg font-medium text-foreground mb-1">Pronto a iniziare</div>
        <div className="text-sm">
          Seleziona un messaggio per leggerlo, oppure premi{' '}
          <kbd className="inline-flex items-center justify-center h-5 px-1.5 rounded border bg-muted text-[11px] font-mono">
            C
          </kbd>{' '}
          per scrivere.
        </div>
        <div className="text-xs mt-3">
          Premi <kbd className="inline-flex items-center justify-center h-5 px-1.5 rounded border bg-muted text-[11px] font-mono">?</kbd>{' '}
          per le scorciatoie da tastiera.
        </div>
      </div>
    </div>
  );
}
